import React from 'react'
import { Helmet } from 'react-helmet-async'
import { IconCheck, IconShieldLock, IconUserCircle } from '@tabler/icons-react'
import AuthHeader from 'src/components/molecules/AuthHeader'
import Typography from 'src/components/atoms/Typography'
import { PrimaryButton } from 'src/components/atoms/buttons/PrimaryButton'
import { SecondaryButton } from 'src/components/atoms/buttons/SecondaryButton'
import AuthWrapper from 'src/enviroment/layouts/auth-wrapper'
import { isLoopbackHost } from 'src/enviroment/utils/isLoopbackHost'

const SCOPE_LABELS: Record<string, string> = {
  'ramo:read': 'View your projects, layers and data',
  'ramo:write': 'Change your projects and layers',
}

export interface OauthConsentTemplateProps {
  clientName: string
  redirectHost: string
  scopes: string[]
  email?: string
  isAllowing: boolean
  isDenying: boolean
  isLeaving: boolean
  onAllow: () => void
  onDeny: () => void
}

const OauthConsentTemplate: React.FC<OauthConsentTemplateProps> = ({
  clientName,
  redirectHost,
  scopes,
  email,
  isAllowing,
  isDenying,
  isLeaving,
  onAllow,
  onDeny,
}) => {
  return (
    <>
      <Helmet>
        <title>RAMO - Connect {clientName}</title>
      </Helmet>
      <AuthWrapper>
        <div className="space-y-8">
          <AuthHeader title="Connect to" subtitle={`${clientName} is asking for access to your RAMO account.`} />

          {email && (
            <div className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3">
              <IconUserCircle className="h-6 w-6 shrink-0 text-light-green-300" />
              <Typography variant="base" className="text-light-green-50">
                Signed in as <span className="font-semibold">{email}</span>
              </Typography>
            </div>
          )}

          <div className="space-y-4 rounded-xl border border-white/10 p-5">
            <Typography variant="xl-bold" className="text-light-green-50">
              {clientName} will be able to:
            </Typography>
            <ul className="space-y-3">
              {scopes.map((scope) => (
                <li key={scope} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-light-green-500/20">
                    <IconCheck className="h-4 w-4 text-light-green-400" />
                  </span>
                  <Typography variant="lg-medium" className="text-light-green-50">
                    {SCOPE_LABELS[scope] ?? scope}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-start gap-3">
            <IconShieldLock className="mt-0.5 h-5 w-5 shrink-0 text-light-green-300" />
            <Typography variant="base" className="text-light-green-100">
              Access goes to{' '}
              <span className="font-semibold text-light-green-50">
                {isLoopbackHost(redirectHost) ? `an app on this computer (${redirectHost})` : redirectHost}
              </span>
              . Allow only if you started connecting {clientName} yourself just now.
            </Typography>
          </div>

          <div className="space-y-4">
            <PrimaryButton fullWidth isLoading={isAllowing} disabled={isLeaving} onClick={onAllow}>
              Allow access
            </PrimaryButton>
            <SecondaryButton fullWidth isLoading={isDenying} disabled={isLeaving} onClick={onDeny}>
              Deny
            </SecondaryButton>
          </div>
        </div>
      </AuthWrapper>
    </>
  )
}

export default OauthConsentTemplate
